import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Target, DollarSign, Users } from 'lucide-react';
import { Heading } from '../layout';
import { LicitationsForm } from '../forms/LicitationsForm';
import LicitationsTable3 from './LicitationsTable3';
import LicitationDetailModal from './LicitationDetailModal';
import { getActiveChannelOptions } from './commercialCatalogDefaults';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';

const EMPTY_FORM = {
  tender_code: '',
  licitation_code: '',
  tender_name: '',
  organization_id: '',
  organization_unit_id: '',
  quotation_ids_text: '',
  closing_date: '',
  adjudication_date: '',
  estimated_amount: '0',
  maximum_amount: '0',
  winning_amount: '',
  winner: '',
  currency: 'CLP',
  channel: '',
  assigned_user: '',
  status: 'Abierta',
  notes: '',
};

const DashboardCard = ({ title, value, helper, icon, isDarkMode = false }) => (
  <div
    style={{
      border: `1px solid ${isDarkMode ? '#2f2f35' : '#e5e7eb'}`,
      borderRadius: '10px',
      padding: '12px',
      background: isDarkMode ? '#18181b' : '#ffffff',
      minHeight: '92px',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <p style={{ margin: 0, fontSize: '12px', color: isDarkMode ? '#a1a1aa' : '#6b7280' }}>{title}</p>
      <span style={{ color: isDarkMode ? '#d4d4d8' : '#374151' }}>{icon}</span>
    </div>
    <p style={{ margin: '8px 0 0', fontSize: '24px', fontWeight: 700, color: isDarkMode ? '#f4f4f5' : '#111827' }}>
      {value}
    </p>
    <p style={{ margin: '6px 0 0', fontSize: '12px', color: isDarkMode ? '#a1a1aa' : '#6b7280' }}>{helper}</p>
  </div>
);

const PaintLicitationsWorkspace = ({ isDarkMode = false }) => {
  const [data, setData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [organizationsData, setOrganizationsData] = useState([]);
  const [organizationUnitsData, setOrganizationUnitsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [detailLicitation, setDetailLicitation] = useState(null);
  const [detailQuotations, setDetailQuotations] = useState([]);
  const [detailItems, setDetailItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadLicitations = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/licitations`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setData(rawData);
    } catch (fetchError) {
      console.error('Error loading licitations:', fetchError);
      setData([]);
      setError('No se pudieron cargar las licitaciones.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/users/`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setUsersData(Array.isArray(rawData) ? rawData : []);
    } catch (fetchError) {
      console.error('Error loading users:', fetchError);
      setUsersData([]);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/organizations/`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setOrganizationsData(Array.isArray(rawData) ? rawData : []);
    } catch (fetchError) {
      console.error('Error loading organizations for licitations:', fetchError);
      setOrganizationsData([]);
    }
  };

  useEffect(() => {
    loadLicitations();
    loadUsers();
    loadOrganizations();
  }, []);

  useEffect(() => {
    const organizationId = Number(formData.organization_id || 0);
    if (!showFormModal || !organizationId) {
      setOrganizationUnitsData([]);
      return;
    }
    let cancelled = false;
    authFetch(`${API_BASE_URL}/api/organizations/${organizationId}/units/`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((rawData) => {
        if (!cancelled) setOrganizationUnitsData(Array.isArray(rawData) ? rawData : []);
      })
      .catch((fetchError) => {
        console.error('Error loading organization units for licitation:', fetchError);
        if (!cancelled) setOrganizationUnitsData([]);
      });
    return () => {
      cancelled = true;
    };
  }, [formData.organization_id, showFormModal]);

  const userOptions = useMemo(
    () =>
      (usersData || [])
        .map((user) => {
          const label = String(user.full_name || user.user_code || '').trim();
          if (!label) return null;
          return { value: label, label };
        })
        .filter(Boolean),
    [usersData]
  );

  const channelOptions = useMemo(() => getActiveChannelOptions(), []);
  const organizationOptions = useMemo(
    () =>
      (organizationsData || [])
        .map((organization) => ({
          value: String(organization.id),
          label: String(organization.org_name || '').trim(),
        }))
        .filter((option) => option.value && option.label)
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [organizationsData]
  );
  const organizationUnitOptions = useMemo(
    () =>
      (organizationUnitsData || []).map((unit) => ({
        value: String(unit.id),
        label: String(unit.unit_name || unit.unit_code || `Subunidad ${unit.id}`).trim(),
      })),
    [organizationUnitsData]
  );

  const metrics = useMemo(() => {
    const total = data.length;
    const open = data.filter((row) => ['Abierta', 'En Analisis', 'Oferta Enviada'].includes(row.status)).length;
    const won = data.filter((row) => String(row.winner || '').trim()).length;
    const estimated = data.reduce((acc, row) => acc + Number(row.estimated_amount || 0), 0);
    const distinctOrgs = new Set(
      data.map((row) => row.organization_id || row.organization_name).filter(Boolean)
    ).size;
    return { total, open, won, estimated, distinctOrgs };
  }, [data]);

  const openCreateForm = () => {
    setDetailLicitation(null);
    setEditingId(null);
    setFormError('');
    setFormData({ ...EMPTY_FORM });
    setShowFormModal(true);
  };

  const openDetail = async (row) => {
    if (!row) return;
    setDetailLicitation(row);
    setDetailQuotations([]);
    setDetailItems([]);
    setDetailLoading(true);
    try {
      const [quotationsRes, itemsRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/quotations/`),
        authFetch(`${API_BASE_URL}/api/quotations/items`),
      ]);
      if (!quotationsRes.ok) throw new Error(`HTTP ${quotationsRes.status}`);
      if (!itemsRes.ok) throw new Error(`HTTP ${itemsRes.status}`);
      const quotationsData = await quotationsRes.json();
      const itemsData = await itemsRes.json();
      const quotationIds = new Set(
        (Array.isArray(row.quotation_ids) ? row.quotation_ids : [])
          .map((id) => Number(id))
          .filter((id) => id > 0)
      );
      const linkedQuotations = (Array.isArray(quotationsData) ? quotationsData : []).filter(
        (quotation) =>
          Number(quotation.licitation_id) === Number(row.id) || quotationIds.has(Number(quotation.id))
      );
      const linkedQuotationIds = new Set(linkedQuotations.map((quotation) => Number(quotation.id)));
      const linkedItems = (Array.isArray(itemsData) ? itemsData : []).filter((item) =>
        linkedQuotationIds.has(Number(item.quotation_id))
      );
      setDetailQuotations(linkedQuotations);
      setDetailItems(linkedItems);
    } catch (fetchError) {
      console.error('Error loading licitation detail relations:', fetchError);
      setDetailQuotations([]);
      setDetailItems([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const openEditForm = (row) => {
    if (!row) return;
    setDetailLicitation(null);
    setEditingId(row.id);
    setFormError('');
    setFormData({
      tender_code: row.tender_code || '',
      licitation_code: row.licitation_code || '',
      tender_name: row.tender_name || '',
      organization_id: row.organization_id ? String(row.organization_id) : '',
      organization_unit_id: row.organization_unit_id ? String(row.organization_unit_id) : '',
      quotation_ids_text: Array.isArray(row.quotation_ids) ? row.quotation_ids.join(', ') : '',
      closing_date: row.closing_date || '',
      adjudication_date: row.adjudication_date || '',
      estimated_amount: String(row.estimated_amount ?? 0),
      maximum_amount: String(row.maximum_amount ?? 0),
      winning_amount: row.winning_amount == null ? '' : String(row.winning_amount),
      winner: row.winner || '',
      currency: row.currency || 'CLP',
      channel: row.channel || '',
      assigned_user: row.assigned_user || '',
      status: row.status || 'Abierta',
      notes: row.notes || '',
    });
    setShowFormModal(true);
  };

  const openEditFromDetail = (row) => {
    openEditForm(row || detailLicitation);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'organization_id' ? { organization_unit_id: '' } : {}),
    }));
  };

  const handleSave = async () => {
    if (!formData.tender_name.trim()) {
      setFormError('El nombre de la licitacion es obligatorio.');
      return;
    }
    if (!Number(formData.organization_id || 0)) {
      setFormError('Selecciona la organizacion de la licitacion.');
      return;
    }
    setIsSaving(true);
    setFormError('');
    const payload = {
      tender_code: formData.tender_code.trim(),
      licitation_code: formData.licitation_code.trim(),
      tender_name: formData.tender_name.trim(),
      organization_id: Number(formData.organization_id),
      organization_unit_id: Number(formData.organization_unit_id || 0),
      closing_date: formData.closing_date || '',
      adjudication_date: formData.adjudication_date || '',
      estimated_amount: Number(formData.estimated_amount || 0),
      maximum_amount: Number(formData.maximum_amount || 0),
      currency: formData.currency || 'CLP',
      channel: formData.channel || '',
      assigned_user: formData.assigned_user.trim(),
      status: formData.status || 'Abierta',
      notes: formData.notes.trim(),
    };
    const endpoint = editingId
      ? `${API_BASE_URL}/api/quotations/licitations/${editingId}`
      : `${API_BASE_URL}/api/quotations/licitations`;
    const method = editingId ? 'PUT' : 'POST';
    try {
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      setShowFormModal(false);
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      await loadLicitations();
    } catch (saveError) {
      setFormError(saveError.message || 'No se pudo guardar la licitacion.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (payload) => {
    const rows = Array.isArray(payload) ? payload : [payload];
    try {
      const requests = rows.map((row) =>
        authFetch(`${API_BASE_URL}/api/quotations/licitations/${row.id}`, { method: 'DELETE' })
      );
      const responses = await Promise.all(requests);
      if (responses.some((response) => !response.ok)) {
        throw new Error('No se pudieron eliminar todos los registros.');
      }
      await loadLicitations();
    } catch (deleteError) {
      window.alert(deleteError.message || 'Error eliminando licitaciones.');
      throw deleteError;
    }
  };

  const handleBulkAssign = async (payload) => {
    const response = await authFetch(`${API_BASE_URL}/api/quotations/licitations/bulk/assign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || `HTTP ${response.status}`);
    }
    await loadLicitations();
  };

  return (
    <div>
      <Heading level={2} isDarkMode={isDarkMode}>
        <FileText size={22} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Licitaciones
      </Heading>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '10px' }}>
        <DashboardCard
          title="Licitaciones Totales"
          value={metrics.total.toLocaleString('es-CL')}
          helper="Base activa"
          icon={<FileText size={16} />}
          isDarkMode={isDarkMode}
        />
        <DashboardCard
          title="Abiertas / En Proceso"
          value={metrics.open.toLocaleString('es-CL')}
          helper={`Adjudicadas: ${metrics.won.toLocaleString('es-CL')}`}
          icon={<Target size={16} />}
          isDarkMode={isDarkMode}
        />
        <DashboardCard
          title="Monto Estimado"
          value={`$ ${metrics.estimated.toLocaleString('es-CL')}`}
          helper="Suma de montos estimados"
          icon={<DollarSign size={16} />}
          isDarkMode={isDarkMode}
        />
        <DashboardCard
          title="Organizaciones"
          value={metrics.distinctOrgs.toLocaleString('es-CL')}
          helper="Clientes en pipeline"
          icon={<Users size={16} />}
          isDarkMode={isDarkMode}
        />
      </div>
      <LicitationsTable3
        rows={data}
        loading={isLoading}
        error={error}
        isDarkMode={isDarkMode}
        onAdd={openCreateForm}
        onRowOpen={openDetail}
        onRowEdit={openEditForm}
        onBulkDelete={handleDelete}
        onBulkAssign={handleBulkAssign}
        userOptions={userOptions}
        channelOptions={channelOptions}
      />
      <LicitationDetailModal
        isOpen={Boolean(detailLicitation)}
        onClose={() => {
          setDetailLicitation(null);
          setDetailQuotations([]);
          setDetailItems([]);
        }}
        licitation={detailLicitation}
        quotations={detailQuotations}
        items={detailItems}
        loading={detailLoading}
        isDarkMode={isDarkMode}
        onEdit={openEditFromDetail}
      />
      <LicitationsForm
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSave={handleSave}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingId)}
        formData={formData}
        onChange={handleFormChange}
        formError={formError}
        channelOptions={channelOptions}
        organizationOptions={organizationOptions}
        organizationUnitOptions={organizationUnitOptions}
      />
    </div>
  );
};

export default PaintLicitationsWorkspace;
